// Carousel.jsx
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { CardMedia, Typography, Grid, Box } from "@mui/material";
import { DESKTOP_DATA2, MOBILE_DATA2 } from "../utils/data2.js";
const clamp = (n, min, max) => Math.max(min, Math.min(n, max));

export default function Carousel({
  itemWidth = 200,
  viewportWidth = 900,
  gap = 12,
  fadeInactive = true,
  scaleActive = 1.0,
  scaleInactive = 0.94,
  loop = false,
  centerActive = true,     // ให้อยู่กึ่งกลาง
  initialIndex = 1,        // เริ่มที่ index 1 = รูปที่ 2 (ปรับได้)
}) {
  // Responsive values
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  // เลือกข้อมูลตาม device
  const isMobile = windowWidth <= 768;
  const items = isMobile ? MOBILE_DATA2 : DESKTOP_DATA2;
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive item width
  const responsiveItemWidth = windowWidth < 768 ? Math.min(itemWidth, windowWidth * 0.8) : itemWidth;
  const responsiveGap = windowWidth < 768 ? Math.min(gap, 8) : gap;
  const containerRef = useRef(null);
  const [active, setActive] = useState(
    clamp(initialIndex, 0, Math.max(items.length - 1, 0))
  );
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const didInit = useRef(false);

  const STEP = responsiveItemWidth + responsiveGap;

  // ค่าชดเชยให้ snap เล็ง "กึ่งกลางคอนเทนเนอร์"
  const getCenterPad = () => {
    if (!centerActive) return 0;
    const el = containerRef.current;
    if (!el) return 0;
    return Math.max(0, (el.clientWidth - responsiveItemWidth) / 2);
  };

  // แปลงตำแหน่งสกรอลล์ <-> index
  const indexFromLeft = (left) => {
    const idx = Math.round((left + getCenterPad()) / STEP);
    return clamp(idx, 0, Math.max(items.length - 1, 0));
  };

  const leftFromIndex = (i) => i * STEP - getCenterPad();

  const syncFromLeft = (left) => {
    const el = containerRef.current;
    if (!el) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);
    setActive(indexFromLeft(left));
    setAtStart(!loop && left <= 0);
    setAtEnd(!loop && left >= max - 1);
  };

  const onScroll = (e) => syncFromLeft(e.currentTarget.scrollLeft);

  const scrollToIndex = (i, behavior = "smooth") => {
    const el = containerRef.current;
    if (!el) return;
    const max = Math.max(0, el.scrollWidth - el.clientWidth);

    let idx = i;
    if (loop) idx = (i + items.length) % items.length;
    else idx = clamp(i, 0, Math.max(items.length - 1, 0));

    const left = clamp(centerActive ? leftFromIndex(idx) : idx * STEP, 0, max);
    el.scrollTo({ left, behavior });
    syncFromLeft(left);
  };

  const scrollByStep = (dir /* -1 / +1 */) => scrollToIndex(active + dir);

  // จัดตำแหน่งครั้งแรก + sync เมื่อพร็อพเปลี่ยน
  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      // รอ layout คำนวณขนาดก่อน ค่อยเลื่อนไป initialIndex ให้ตรงกลางพอดี
      requestAnimationFrame(() => scrollToIndex(active, "auto"));
      return;
    }
    syncFromLeft(containerRef.current?.scrollLeft ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, itemWidth, gap, loop, centerActive, viewportWidth]);

  // จัดให้อยู่กลางเดิมเมื่อ resize
  useEffect(() => {
    const onResize = () => {
      if (centerActive) requestAnimationFrame(() => scrollToIndex(active, "auto"));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [active, centerActive]);

  if (!items || items.length === 0) return null;

  return (
    <div
      className="hs-wrapper"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: `${viewportWidth}px`,
        margin: "0 auto",
      }}
    >
      <Grid
        item
        xs={12}
        md={4}
        data-aos="fade-right"
        sx={{ mt: { xs: "5%", md: "10%" }, display: "block" }}
      >
        <Typography
          variant="h5"
          component="div"
          sx={{
            width: { xs: "95%", sm: "90%", md: "85%" },
            mx: "auto",
            display: "block",
            textAlign: "center",
            fontWeight: "normal",
            color: "#052c81",
            lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
            padding: { xs: "15px", sm: "18px", md: "20px" },
            fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "22px" }
          }}
        >
          เป้าหมายทางการเงิน ณ เกษียณ (MLS) จะช่วยให้สมาชิกรู้ว่า <span style={{ 
            fontWeight: "bold", 
            fontSize: "clamp(18px, 4vw, 26px)", 
            color: "#4884ff",
            display: "inline-block",
            margin: "8px 0"
          }}>จำนวนเงินที่สมาชิก "ควรจะมี" เป็นเท่าไหร่?</span>
          <br />
          โดย MLS แบ่งระดับคุณภาพชีวิตหลังเกษียณเป็น 3 ระดับ คือ ระดับพื้นฐานทั่วไป ระดับดี และ ระดับดีมาก
        </Typography>
      </Grid>
      <div
        ref={containerRef}
        onScroll={onScroll}
        style={{
          overflowX: isMobile ? "hidden" : "auto",
          scrollSnapType: isMobile ? "none" : "x mandatory",
          // ให้ snap เล็งกึ่งกลาง (เฉพาะโหมด centerActive)
          scrollPaddingLeft: centerActive && !isMobile
            ? `calc((100% - ${responsiveItemWidth}px) / 2)`
            : undefined,
          scrollPaddingRight: centerActive && !isMobile
            ? `calc((100% - ${responsiveItemWidth}px) / 2)`
            : undefined,
          paddingBottom: windowWidth < 768 ? 4 : 8,
          paddingLeft: windowWidth < 768 ? 8 : 0,
          paddingRight: windowWidth < 768 ? 8 : 0,
          // Hide scrollbar on mobile
          scrollbarWidth: windowWidth < 768 ? "none" : "auto",
          msOverflowStyle: windowWidth < 768 ? "none" : "auto",
          "&::-webkit-scrollbar": {
            display: windowWidth < 768 ? "none" : "block"
          }
        }}
      >
        <div className="content-box" style={{ 
          display: isMobile ? "block" : "flex", 
          gap: responsiveGap 
        }}>
          {items.map((item, i) => {
            const src = item.img || item.image || item.src || item.url;
            return (
              <div
                key={item.id ?? i}
                className={`card ${i === active ? "active" : ""}`}
                style={{
                  minWidth: isMobile ? "100%" : responsiveItemWidth,
                  width: isMobile ? "100%" : "auto",
                  height: isMobile ? Math.min(item.height ?? 300, windowWidth * 0.6) : (item.height ?? 500),
                  borderRadius: 0,
                  scrollSnapAlign: isMobile ? "none" : (centerActive ? "center" : "start"),
                  scrollSnapStop: isMobile ? "none" : "always",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "opacity .25s ease, transform .25s ease",
                  opacity: isMobile ? 1 : (fadeInactive ? (i === active ? 1 : 0.35) : 1),
                  transform: isMobile ? "none" : `scale(${i === active ? scaleActive : scaleInactive})`,
                  overflow: "hidden",
                  boxShadow: "none",
                  border: "none",
                  backgroundColor: "transparent",
                  marginBottom: isMobile ? "16px" : "0",
                }}
              >
                {src ? (
                  <img
                    src={src}
                    alt={item.alt || `slide ${i + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                    loading="lazy"
                    draggable={false}
                  />
                ) : (
                  <p style={{ margin: 0 }}>{item.id ?? i + 1}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ปุ่มซ้าย/ขวาแบบ overlay - ซ่อนใน mobile */}
      {!isMobile && (
        <div
          className="hs-arrows"
          style={{
            pointerEvents: "none",
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 4,
          }}
        >
        <IconButton
          aria-label="Previous"
          onClick={() => scrollByStep(-1)}
          disabled={atStart && !loop}
          size="small"
          sx={{
            pointerEvents: "auto",
            bgcolor: "rgba(255,255,255,0.85)",
            boxShadow: 1,
            "&:hover": { bgcolor: "white" },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <IconButton
          aria-label="Next"
          onClick={() => scrollByStep(1)}
          disabled={atEnd && !loop}
          size="small"
          sx={{
            pointerEvents: "auto",
            bgcolor: "rgba(255,255,255,0.85)",
            boxShadow: 1,
            "&:hover": { bgcolor: "white" },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
        </div>
      )}

      <Typography
        variant="h5"
        component="div"
        sx={{
          width: { xs: "95%", sm: "90%", md: "85%" },
          mx: "auto",
          display: "block",
          textAlign: "center",
          fontWeight: "normal",
          color: "#052c81",
          lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
          padding: { xs: "15px", sm: "18px", md: "20px" },
          fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "22px" }
        }}
      >
        โดย MLS ระดับดี (P75) คือ เป้าหมายมาตรฐานที่ กบข. แนะนำให้สมาชิกตั้งไว้เพื่อให้มีคุณภาพชีวิตที่ "ดีพอ" ในวัยเกษียณ
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // มือถือเรียงลง, จอกว้างเรียงข้าง
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          gap: 0, // ไม่มีช่องว่างระหว่างภาพ
        }}
      >
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          gap: { xs: 1, sm: 2, md: 3 }, 
          flexWrap: "wrap",
          flexDirection: { xs: "column", sm: "row" },
          textAlign: "center"
        }}>
          <Typography
            variant="body1"
            component="div"
            sx={{
              color: "#052c81",
              fontWeight: "350",
              fontSize: { xs: "16px", sm: "18px", md: "20px", lg: "22px" },
              lineHeight: 1.4,
              marginBottom: { xs: 1, sm: 0 }
            }}
          >
            อ่านเพิ่มเติมเกี่ยวกับระดับ MLS
          </Typography>
          <CardMedia
            component="img"
            image="https://gpfimg-cdn.azureedge.net/micrositemygpftwins/phase3/desktop/page2/Asset009.png"
            alt="อ่านเพิ่มเติม"
            onClick={() => window.open("https://cfl.gpf.or.th/Files/Viewer/?fileName=c6a4db6c-0ed2-4ea0-971b-ac40089f20e1/GPF-Annual-Report-2024-(1)-83-87.pdf", "_blank")}
            sx={{
              width: { xs: "60%", sm: "30%", md: "25%", lg: "20%" },
              maxWidth: { xs: "200px", sm: "150px", md: "120px" },
              height: "auto",
              mx: 0,
              display: "block",
              cursor: "pointer",
              transition: "transform 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05)"
              }
            }}
          />
        </Box>
      </Box>
    </div>
  );
}

Carousel.propTypes = {
  itemWidth: PropTypes.number,
  viewportWidth: PropTypes.number,
  gap: PropTypes.number,
  fadeInactive: PropTypes.bool,
  scaleActive: PropTypes.number,
  scaleInactive: PropTypes.number,
  loop: PropTypes.bool,
  centerActive: PropTypes.bool,
  initialIndex: PropTypes.number,
};
